import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Info, Eye, DollarSign, Sparkles } from "lucide-react";

/**
 * Experience Layer v1 — Pre-chat onboarding shell.
 *
 * Two screens:
 *  1. Trust & expectations — tenant-driven disclaimer blocks + ack checkbox.
 *  2. Optional personalization — name / company / email / phone, all skippable.
 *
 * Architecture:
 *  - 100% driven by the tenant's `onboarding_config` (see backend
 *    `_resolve_onboarding_config`). Restaurant tenants who keep
 *    `enabled = false` (or omit the field) bypass this entirely.
 *  - Personalization values are session-scoped only — handed back to the
 *    parent as `visitor_context` and sent with every `/chat` request.
 *  - We persist the accepted state + collected context to sessionStorage
 *    keyed by slug so a tab refresh doesn't make the visitor re-onboard.
 *
 * Renders nothing if the parent decides onboarding is not required.
 */
const PERSONALIZATION_FIELD_META = {
  name:    { label: "Your name",            type: "text",  placeholder: "Jane Doe",          required: false },
  company: { label: "Company / practice",   type: "text",  placeholder: "Acme Inc.",         required: false },
  email:   { label: "Email",                type: "email", placeholder: "jane@acme.com",     required: false },
  phone:   { label: "Phone (optional)",     type: "tel",   placeholder: "+1 555 123 4567",   required: false },
};

export default function ChatOnboarding({
  config,
  slug,
  companyName,
  assistantName,
  onComplete,
  inline = false,    // NEW — when true, render as in-thread assistant cards
                     // (no fullscreen shell). The header/sidebar/input around
                     // it stay sticky in PublicChatbot.jsx.
}) {
  const showPersonalization = Boolean(config?.personalization?.enabled);
  const requireAck = Boolean(config?.require_acknowledgement ?? true);

  // Two screens: 1 = trust, 2 = personalization (skipped if disabled).
  const [step, setStep] = useState(1);
  const [acked, setAcked] = useState(false);
  const [visitor, setVisitor] = useState({ name: "", company: "", email: "", phone: "" });

  const personalizationFields = useMemo(() => {
    const ids = Array.isArray(config?.personalization?.fields)
      ? config.personalization.fields
      : ["name", "company", "email", "phone"];
    return ids.filter((id) => PERSONALIZATION_FIELD_META[id]);
  }, [config]);

  // Restore any partial state from a previous tab visit in the same session.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`rch:onboarding:${slug}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.visitor) setVisitor((v) => ({ ...v, ...parsed.visitor }));
      }
    } catch (_) { /* no-op */ }
  }, [slug]);

  const persist = (visitorCtx) => {
    try {
      sessionStorage.setItem(
        `rch:onboarding:${slug}`,
        JSON.stringify({ completed: true, visitor: visitorCtx, ts: Date.now() })
      );
    } catch (_) { /* quota — non-fatal */ }
  };

  const finish = (collected) => {
    const clean = {};
    Object.entries(collected || {}).forEach(([k, v]) => {
      if (typeof v === "string" && v.trim()) clean[k] = v.trim();
    });
    persist(clean);
    onComplete?.(clean);
  };

  const advanceFromTrust = () => {
    if (requireAck && !acked) return;
    if (showPersonalization) {
      setStep(2);
    } else {
      finish({});
    }
  };

  const submitPersonalization = (e) => {
    e?.preventDefault?.();
    finish(visitor);
  };

  const skipPersonalization = () => finish({});

  // ---- Render ----
  const personalization = config?.personalization || {};
  const startLabel = config?.start_label || "Start chatting";
  const ackLabel = config?.acknowledgement_label
    || "I understand and would like to start the conversation.";

  if (step === 2) {
    const personalizationBody = (
      <>
        <div className="rch-onboarding-eyebrow">
          <Sparkles className="w-3.5 h-3.5" aria-hidden /> Step 2 of 2
        </div>
        <h1 className="rch-onboarding-title" data-testid="rch-onboarding-title">
          {personalization.title || "Tell us a bit about you"}
        </h1>
        <p className="rch-onboarding-sub">
          {personalization.subtitle
            || "Optional — helps us tailor the conversation. You can skip this."}
        </p>

        <form className="rch-onboarding-form" onSubmit={submitPersonalization}>
          {personalizationFields.map((id) => {
            const meta = PERSONALIZATION_FIELD_META[id];
            return (
              <label className="rch-onboarding-field" key={id}>
                <span className="rch-onboarding-label">{meta.label}</span>
                <input
                  type={meta.type}
                  placeholder={meta.placeholder}
                  value={visitor[id] || ""}
                  onChange={(e) =>
                    setVisitor((v) => ({ ...v, [id]: e.target.value }))
                  }
                  data-testid={`rch-onboarding-input-${id}`}
                  autoComplete={id === "email" ? "email" : id === "phone" ? "tel" : "off"}
                />
              </label>
            );
          })}

          <div className="rch-onboarding-actions">
            <button
              type="submit"
              className="rch-onboarding-primary"
              data-testid="rch-onboarding-continue"
            >
              {personalization.continue_label || startLabel}
            </button>
            {(personalization.skippable ?? true) && (
              <button
                type="button"
                className="rch-onboarding-ghost"
                onClick={skipPersonalization}
                data-testid="rch-onboarding-skip"
              >
                {personalization.skip_label || "Skip — start chatting"}
              </button>
            )}
          </div>
        </form>

        <p className="rch-onboarding-footnote">
          We store this only for the current conversation. No account is created.
        </p>
      </>
    );

    if (inline) {
      return (
        <div className="rch-onboarding-inline" data-testid="rch-onboarding-personalization">
          <div className="rch-onboarding-inline-avatar" aria-hidden>
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="rch-onboarding-inline-card">
            {personalizationBody}
          </div>
        </div>
      );
    }

    return (
      <div className="rch-onboarding-shell" data-testid="rch-onboarding-personalization">
        <div className="rch-onboarding-card">
          {personalizationBody}
        </div>
      </div>
    );
  }

  // Screen 1 — trust & expectations.
  const blocks = [];
  if (config?.show_phi_warning) {
    blocks.push({
      icon: <ShieldCheck className="w-4 h-4" aria-hidden />,
      title: "Avoid sharing sensitive personal or health data",
      body:
        "This is a general AI assistant. Please don't share PHI, ID numbers, or any data subject to HIPAA / GDPR / similar laws.",
    });
  }
  if (config?.show_logging_notice) {
    blocks.push({
      icon: <Eye className="w-4 h-4" aria-hidden />,
      title: "Conversations may be reviewed",
      body:
        `Your messages with ${companyName || "this business"} are logged and may be reviewed by our team to improve responses and follow up if you ask us to.`,
    });
  }
  if (config?.show_pricing_notice) {
    blocks.push({
      icon: <DollarSign className="w-4 h-4" aria-hidden />,
      title: "Pricing is customized",
      body:
        "Any figures shared are indicative. Final pricing depends on your specific situation — our team will confirm before anything is committed.",
    });
  }
  // Custom tenant blocks (already shape-validated server-side).
  (config?.disclaimer_blocks || []).forEach((b) => {
    if (b?.title || b?.body) {
      blocks.push({ icon: <Info className="w-4 h-4" aria-hidden />, ...b });
    }
  });

  const trustBody = (
    <>
      <div className="rch-onboarding-eyebrow">
        <Sparkles className="w-3.5 h-3.5" aria-hidden />
        {showPersonalization ? " Step 1 of 2" : " Before we start"}
      </div>
      <h1 className="rch-onboarding-title">
        {config?.headline || "Before we start"}
      </h1>
      <p className="rch-onboarding-sub">
        {config?.subhead
          || `A quick heads-up before chatting with ${assistantName || (companyName ? `${companyName}'s assistant` : "our AI assistant")}.`}
      </p>

      <div className="rch-onboarding-blocks">
        <div className="rch-onboarding-block">
          <div className="rch-onboarding-block-icon"><Sparkles className="w-4 h-4" aria-hidden /></div>
          <div>
            <div className="rch-onboarding-block-title">You're chatting with an AI assistant</div>
            <div className="rch-onboarding-block-body">
              {assistantName ? `${assistantName} is an AI` : "This is an AI assistant"}, not a human. It can make mistakes — please verify anything important.
            </div>
          </div>
        </div>
        {blocks.map((b, i) => (
          <div className="rch-onboarding-block" key={i}>
            <div className="rch-onboarding-block-icon">{b.icon}</div>
            <div>
              <div className="rch-onboarding-block-title">{b.title}</div>
              <div className="rch-onboarding-block-body">{b.body}</div>
            </div>
          </div>
        ))}
      </div>

      {requireAck && (
        <label className="rch-onboarding-ack" data-testid="rch-onboarding-ack-label">
          <input
            type="checkbox"
            checked={acked}
            onChange={(e) => setAcked(e.target.checked)}
            data-testid="rch-onboarding-ack"
          />
          <span>{ackLabel}</span>
        </label>
      )}

      <button
        type="button"
        className="rch-onboarding-primary"
        onClick={advanceFromTrust}
        disabled={requireAck && !acked}
        data-testid="rch-onboarding-start"
      >
        {showPersonalization ? "Continue" : startLabel}
      </button>
    </>
  );

  if (inline) {
    return (
      <div className="rch-onboarding-inline" data-testid="rch-onboarding-trust">
        <div className="rch-onboarding-inline-avatar" aria-hidden>
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="rch-onboarding-inline-card">
          {trustBody}
        </div>
      </div>
    );
  }

  return (
    <div className="rch-onboarding-shell" data-testid="rch-onboarding-trust">
      <div className="rch-onboarding-card">
        {trustBody}
      </div>
    </div>
  );
}
